import { flushBufferedLogEvents } from '@/utils/logBuffer';
import { Platform } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import {
  addSentryBreadcrumb,
  captureSentryException,
  captureSentryMessage,
} from '../../services/monitoring/sentry';
import {
  clearPendingCameraContext,
  persistPendingCameraContext,
} from './pendingImagePickerRecovery';
import { requestInAppCamera } from './inAppCamera';

export type PickImageOutcome =
  | { status: 'success'; result: ImagePicker.ImagePickerSuccessResult }
  | { status: 'cancelled' }
  | { status: 'denied' }
  | { status: 'failed'; error: Error };

export type PickImageTelemetryContext = {
  source?: string;
  screen?: string;
  action?: string;
  entityType?: string;
  entityId?: string | null;
  [key: string]: unknown;
};

const UNKNOWN_ANDROID_VERSION_FALLBACK = 33;
const PICKER_LIBRARY_BREADCRUMB_CATEGORY = 'image_picker.gallery';
const PICKER_CAMERA_BREADCRUMB_CATEGORY = 'image_picker.camera';

type PermissionSnapshot = {
  status: string;
  granted: boolean;
  canAskAgain: boolean;
  accessPrivileges: string | null;
  expires: string | number | null;
};

type CameraPermissionSnapshot = {
  status: string;
  granted: boolean;
  canAskAgain: boolean;
  expires: string | number | null;
};

const resolveAndroidVersion = (): number => {
  if (Platform.OS !== 'android') {
    return 0;
  }
  const version = Platform.Version;
  const numericVersion =
    typeof version === 'string' ? Number.parseInt(version, 10) : version;

  return Number.isFinite(numericVersion)
    ? numericVersion
    : UNKNOWN_ANDROID_VERSION_FALLBACK;
};

const normalizeResult = (
  pickerResult: ImagePicker.ImagePickerResult
): PickImageOutcome => {
  if (pickerResult.canceled) {
    return { status: 'cancelled' };
  }

  if ('assets' in pickerResult && pickerResult.assets?.length) {
    return {
      status: 'success',
      result: {
        ...pickerResult,
        canceled: false,
        assets: pickerResult.assets,
      },
    };
  }

  return { status: 'cancelled' };
};

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  return new Error(String(error));
};

const sanitizeUri = (uri: string | null | undefined): string | null => {
  if (!uri) {
    return null;
  }

  if (uri.startsWith('data:')) {
    return 'data://inline';
  }

  const withoutQuery = uri.split('?')[0]?.replace(/\\/g, '/');
  const schemeMatch = withoutQuery.match(/^([a-zA-Z0-9+.-]+):\/\//);
  const scheme = schemeMatch?.[1] ?? 'unknown';
  const fileName = withoutQuery.split('/').filter(Boolean).pop() ?? null;

  if (!fileName) {
    return `${scheme}://...`;
  }

  return `${scheme}://.../${fileName}`;
};

const snapshotPermission = (
  permission: ImagePicker.MediaLibraryPermissionResponse | null | undefined
): PermissionSnapshot | null => {
  if (!permission) {
    return null;
  }

  return {
    status: permission.status,
    granted: !!permission.granted,
    canAskAgain: !!permission.canAskAgain,
    accessPrivileges: permission.accessPrivileges ?? null,
    expires: permission.expires ?? null,
  };
};

const snapshotCameraPermission = (
  permission: ImagePicker.PermissionResponse | null | undefined
): CameraPermissionSnapshot | null => {
  if (!permission) {
    return null;
  }

  return {
    status: permission.status,
    granted: !!permission.granted,
    canAskAgain: !!permission.canAskAgain,
    expires: permission.expires ?? null,
  };
};

const flattenPermissionSnapshot = (
  prefix: 'before' | 'after',
  snapshot: PermissionSnapshot | null
): Record<string, unknown> => {
  if (!snapshot) {
    return {};
  }

  return {
    [`${prefix}PermissionStatus`]: snapshot.status,
    [`${prefix}PermissionGranted`]: snapshot.granted,
    [`${prefix}PermissionCanAskAgain`]: snapshot.canAskAgain,
    [`${prefix}PermissionAccessPrivileges`]: snapshot.accessPrivileges,
    [`${prefix}PermissionExpires`]: snapshot.expires,
  };
};

const flattenCameraPermissionSnapshot = (
  prefix: 'before' | 'after',
  snapshot: CameraPermissionSnapshot | null
): Record<string, unknown> => {
  if (!snapshot) {
    return {};
  }

  return {
    [`${prefix}CameraPermissionStatus`]: snapshot.status,
    [`${prefix}CameraPermissionGranted`]: snapshot.granted,
    [`${prefix}CameraPermissionCanAskAgain`]: snapshot.canAskAgain,
    [`${prefix}CameraPermissionExpires`]: snapshot.expires,
  };
};

const summarizeOptions = (options: ImagePicker.ImagePickerOptions): Record<string, unknown> => ({
  optionAllowsEditing: options.allowsEditing ?? null,
  optionQuality: typeof options.quality === 'number' ? options.quality : null,
  optionAspect: Array.isArray(options.aspect) ? options.aspect.join('x') : null,
  optionAllowsMultipleSelection:
    (options as ImagePicker.ImagePickerOptions & { allowsMultipleSelection?: boolean })
      .allowsMultipleSelection ?? null,
  optionSelectionLimit:
    (options as ImagePicker.ImagePickerOptions & { selectionLimit?: number }).selectionLimit ??
    null,
});

const summarizeResult = (
  result: PickImageOutcome
): Record<string, unknown> => {
  if (result.status !== 'success') {
    return {
      pickerOutcome: result.status,
    };
  }

  const firstAsset = result.result.assets?.[0];
  const assetWithExtras = firstAsset as
    | (ImagePicker.ImagePickerAsset & {
        fileName?: string | null;
        fileSize?: number | null;
        mimeType?: string | null;
        assetId?: string | null;
      })
    | undefined;

  return {
    pickerOutcome: result.status,
    assetsCount: result.result.assets?.length ?? 0,
    firstAssetUri: sanitizeUri(firstAsset?.uri),
    firstAssetWidth: firstAsset?.width ?? null,
    firstAssetHeight: firstAsset?.height ?? null,
    firstAssetType: firstAsset?.type ?? null,
    firstAssetFileName: assetWithExtras?.fileName ?? null,
    firstAssetMimeType: assetWithExtras?.mimeType ?? null,
    firstAssetFileSize: assetWithExtras?.fileSize ?? null,
    firstAssetId: assetWithExtras?.assetId ?? null,
  };
};

const createFlowId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const addPickerBreadcrumb = (
  category: string,
  message: string,
  level: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug',
  data: Record<string, unknown>
): void => {
  addSentryBreadcrumb({
    category,
    message,
    level,
    data,
  });
};

const getCurrentMediaPermission = async (): Promise<ImagePicker.MediaLibraryPermissionResponse | null> => {
  const pickerWithPermissionGetter = ImagePicker as typeof ImagePicker & {
    getMediaLibraryPermissionsAsync?: () => Promise<ImagePicker.MediaLibraryPermissionResponse>;
  };

  if (typeof pickerWithPermissionGetter.getMediaLibraryPermissionsAsync !== 'function') {
    return null;
  }

  try {
    return await pickerWithPermissionGetter.getMediaLibraryPermissionsAsync();
  } catch {
    return null;
  }
};

const getCurrentCameraPermission = async (): Promise<ImagePicker.PermissionResponse | null> => {
  const pickerWithPermissionGetter = ImagePicker as typeof ImagePicker & {
    getCameraPermissionsAsync?: () => Promise<ImagePicker.PermissionResponse>;
  };

  if (typeof pickerWithPermissionGetter.getCameraPermissionsAsync !== 'function') {
    return null;
  }

  try {
    return await pickerWithPermissionGetter.getCameraPermissionsAsync();
  } catch {
    return null;
  }
};

const hasMediaLibraryPermission = (
  permission: ImagePicker.MediaLibraryPermissionResponse
): boolean => {
  if (permission.granted) {
    return true;
  }

  if (permission.status === 'granted') {
    return true;
  }

  return permission.accessPrivileges === 'limited';
};

const launchLibrary = async (
  options: ImagePicker.ImagePickerOptions
): Promise<PickImageOutcome> => {
  flushBufferedLogEvents();
  const result = await ImagePicker.launchImageLibraryAsync(options);
  return normalizeResult(result);
};

const launchCamera = async (
  options: ImagePicker.ImagePickerOptions
): Promise<PickImageOutcome> => {
  const result = await ImagePicker.launchCameraAsync(options);
  return normalizeResult(result);
};

const launchWithDeniedFallback = async (
  options: ImagePicker.ImagePickerOptions,
  telemetryData: Record<string, unknown>
): Promise<PickImageOutcome> => {
  addPickerBreadcrumb(
    PICKER_LIBRARY_BREADCRUMB_CATEGORY,
    'Permissão não concedida. Tentando abrir a galeria via fallback.',
    'warning',
    telemetryData
  );

  try {
    const fallbackOutcome = await launchLibrary(options);

    addPickerBreadcrumb(
      PICKER_LIBRARY_BREADCRUMB_CATEGORY,
      'Fallback da galeria executado após permissão negada.',
      fallbackOutcome.status === 'failed' ? 'error' : 'warning',
      {
        ...telemetryData,
        ...summarizeResult(fallbackOutcome),
      }
    );

    return fallbackOutcome;
  } catch (error) {
    const normalizedError = normalizeError(error);
    addPickerBreadcrumb(PICKER_LIBRARY_BREADCRUMB_CATEGORY, 'Fallback da galeria falhou após permissão negada.', 'error', {
      ...telemetryData,
      fallbackError: normalizedError.message,
    });
    return { status: 'denied' };
  }
};

/**
 * Abre a galeria respeitando a política de acesso limitado do Android.
 * Em Android 13+ usa o Photo Picker sem solicitar permissão explícita.
 */
export const pickImageFromLibrary = async (
  options: ImagePicker.ImagePickerOptions,
  telemetryContext: PickImageTelemetryContext = {}
): Promise<PickImageOutcome> => {
  const androidVersion = resolveAndroidVersion();
  const flowId = createFlowId();
  const baseTelemetry = {
    flowId,
    platform: Platform.OS,
    platformVersion: String(Platform.Version),
    androidApiLevel: Platform.OS === 'android' ? androidVersion : null,
    ...summarizeOptions(options),
    ...telemetryContext,
  };

  addPickerBreadcrumb(
    PICKER_LIBRARY_BREADCRUMB_CATEGORY,
    'Iniciando fluxo de seleção de imagem da galeria.',
    'info',
    baseTelemetry
  );

  try {
    const mergedOptions = {
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      ...options,
    };

    if (Platform.OS === 'android') {
      if (androidVersion >= 33) {
        addPickerBreadcrumb(
          PICKER_LIBRARY_BREADCRUMB_CATEGORY,
          'Android 13+ detectado. Photo Picker sem solicitação explícita de permissão.',
          'info',
          baseTelemetry
        );

        const pickerOutcome = await launchLibrary(mergedOptions);
        addPickerBreadcrumb(PICKER_LIBRARY_BREADCRUMB_CATEGORY, 'Fluxo de galeria concluído.', 'info', {
          ...baseTelemetry,
          ...summarizeResult(pickerOutcome),
        });
        return pickerOutcome;
      }

      const currentPermission = await getCurrentMediaPermission();
      const currentPermissionSnapshot = snapshotPermission(currentPermission);
      addPickerBreadcrumb(PICKER_LIBRARY_BREADCRUMB_CATEGORY, 'Estado atual da permissão de fotos.', 'info', {
        ...baseTelemetry,
        ...flattenPermissionSnapshot('before', currentPermissionSnapshot),
      });

      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      const requestedPermissionSnapshot = snapshotPermission(permission);
      addPickerBreadcrumb(PICKER_LIBRARY_BREADCRUMB_CATEGORY, 'Resultado da solicitação de permissão de fotos.', 'info', {
        ...baseTelemetry,
        ...flattenPermissionSnapshot('after', requestedPermissionSnapshot),
      });

      if (!hasMediaLibraryPermission(permission)) {
        const deniedTelemetry = {
          ...baseTelemetry,
          ...flattenPermissionSnapshot('before', currentPermissionSnapshot),
          ...flattenPermissionSnapshot('after', requestedPermissionSnapshot),
          permissionDeniedReason:
            permission.canAskAgain === false
              ? 'blocked_or_never_ask_again'
              : 'denied_or_limited_not_accepted',
        };

        const deniedOutcome = await launchWithDeniedFallback(mergedOptions, deniedTelemetry);
        if (deniedOutcome.status === 'denied') {
          captureSentryMessage(
            'Acesso à galeria negado ao selecionar imagem (Android < 13).',
            'error',
            deniedTelemetry
          );
        }
        return deniedOutcome;
      }

      const pickerOutcome = await launchLibrary(mergedOptions);
      addPickerBreadcrumb(PICKER_LIBRARY_BREADCRUMB_CATEGORY, 'Fluxo de galeria concluído.', 'info', {
        ...baseTelemetry,
        ...summarizeResult(pickerOutcome),
      });
      return pickerOutcome;
    }

    if (Platform.OS === 'ios') {
      const currentPermission = await getCurrentMediaPermission();
      const currentPermissionSnapshot = snapshotPermission(currentPermission);
      addPickerBreadcrumb(PICKER_LIBRARY_BREADCRUMB_CATEGORY, 'Estado atual da permissão de fotos.', 'info', {
        ...baseTelemetry,
        ...flattenPermissionSnapshot('before', currentPermissionSnapshot),
      });

      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      const requestedPermissionSnapshot = snapshotPermission(permission);
      addPickerBreadcrumb(PICKER_LIBRARY_BREADCRUMB_CATEGORY, 'Resultado da solicitação de permissão de fotos.', 'info', {
        ...baseTelemetry,
        ...flattenPermissionSnapshot('after', requestedPermissionSnapshot),
      });

      if (!hasMediaLibraryPermission(permission)) {
        const deniedTelemetry = {
          ...baseTelemetry,
          ...flattenPermissionSnapshot('before', currentPermissionSnapshot),
          ...flattenPermissionSnapshot('after', requestedPermissionSnapshot),
          permissionDeniedReason:
            permission.canAskAgain === false
              ? 'blocked_or_settings_required'
              : 'denied_or_limited_not_accepted',
        };

        const deniedOutcome = await launchWithDeniedFallback(mergedOptions, deniedTelemetry);
        if (deniedOutcome.status === 'denied') {
          captureSentryMessage(
            'Acesso à galeria negado ao selecionar imagem (iOS).',
            'error',
            deniedTelemetry
          );
        }
        return deniedOutcome;
      }

      const pickerOutcome = await launchLibrary(mergedOptions);
      addPickerBreadcrumb(PICKER_LIBRARY_BREADCRUMB_CATEGORY, 'Fluxo de galeria concluído.', 'info', {
        ...baseTelemetry,
        ...summarizeResult(pickerOutcome),
      });
      return pickerOutcome;
    }

    const pickerOutcome = await launchLibrary(mergedOptions);
    addPickerBreadcrumb(PICKER_LIBRARY_BREADCRUMB_CATEGORY, 'Fluxo de galeria concluído.', 'info', {
      ...baseTelemetry,
      ...summarizeResult(pickerOutcome),
    });
    return pickerOutcome;
  } catch (error) {
    const normalizedError = normalizeError(error);
    addPickerBreadcrumb(PICKER_LIBRARY_BREADCRUMB_CATEGORY, 'Falha inesperada ao abrir galeria.', 'error', {
      ...baseTelemetry,
      exceptionMessage: normalizedError.message,
    });
    captureSentryException(normalizedError, {
      ...baseTelemetry,
      flowStage: 'pickImageFromLibrary',
    });
    return { status: 'failed', error: normalizedError };
  }
};

/**
 * Abre a câmera com rastreamento completo de permissões e resultado.
 */
export const takePhotoFromCamera = async (
  options: ImagePicker.ImagePickerOptions,
  telemetryContext: PickImageTelemetryContext = {}
): Promise<PickImageOutcome> => {
  const androidVersion = resolveAndroidVersion();
  const flowId = createFlowId();
  const baseTelemetry = {
    flowId,
    platform: Platform.OS,
    platformVersion: String(Platform.Version),
    androidApiLevel: Platform.OS === 'android' ? androidVersion : null,
    ...summarizeOptions(options),
    ...telemetryContext,
  };

  addPickerBreadcrumb(
    PICKER_CAMERA_BREADCRUMB_CATEGORY,
    'Iniciando fluxo de captura pela câmera.',
    'info',
    baseTelemetry
  );

  try {
    const mergedOptions = {
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      ...options,
    };

    const currentPermission = await getCurrentCameraPermission();
    const currentPermissionSnapshot = snapshotCameraPermission(currentPermission);
    addPickerBreadcrumb(PICKER_CAMERA_BREADCRUMB_CATEGORY, 'Estado atual da permissão de câmera.', 'info', {
      ...baseTelemetry,
      ...flattenCameraPermissionSnapshot('before', currentPermissionSnapshot),
    });

    const isCameraPermissionAlreadyGranted =
      currentPermissionSnapshot?.granted || currentPermissionSnapshot?.status === 'granted';
    const permissionRequestMode = isCameraPermissionAlreadyGranted
      ? 'already_granted_skipped_request'
      : 'runtime_request';
    const permission = isCameraPermissionAlreadyGranted && currentPermission
      ? currentPermission
      : await ImagePicker.requestCameraPermissionsAsync();
    const requestedPermissionSnapshot = snapshotCameraPermission(permission);
    addPickerBreadcrumb(PICKER_CAMERA_BREADCRUMB_CATEGORY, 'Resultado da solicitação de permissão de câmera.', 'info', {
      ...baseTelemetry,
      permissionRequestMode,
      ...flattenCameraPermissionSnapshot('after', requestedPermissionSnapshot),
    });

    if (!(permission.granted || permission.status === 'granted')) {
      const deniedTelemetry = {
        ...baseTelemetry,
        permissionRequestMode,
        ...flattenCameraPermissionSnapshot('before', currentPermissionSnapshot),
        ...flattenCameraPermissionSnapshot('after', requestedPermissionSnapshot),
        permissionDeniedReason:
          permission.canAskAgain === false
            ? 'blocked_or_settings_required'
            : 'denied',
      };

      captureSentryMessage(
        'Permissão da câmera negada ao capturar foto.',
        'error',
        deniedTelemetry
      );

      return { status: 'denied' };
    }

    const shouldUseInAppCamera =
      Platform.OS === 'ios' ||
      Platform.OS === 'web' ||
      (Platform.OS === 'android' && telemetryContext.source === 'checklist_photo_field');

    if (shouldUseInAppCamera) {
      // O fluxo interno não usa o retorno pendente do ImagePicker externo.
      // Remova qualquer contexto antigo antes de abrir a câmera para evitar
      // que uma sessão anterior tente redirecionar o editor atual.
      await clearPendingCameraContext('in_app_camera_started');
      addPickerBreadcrumb(
        PICKER_CAMERA_BREADCRUMB_CATEGORY,
        Platform.OS === 'android'
          ? 'Abrindo câmera interna do app no Android para foto de checklist.'
          : 'Abrindo câmera interna do app.',
        'info',
        {
          ...baseTelemetry,
          permissionRequestMode,
          inAppCamera: true,
        }
      );

      flushBufferedLogEvents();
      const inAppOutcome = await requestInAppCamera({
        options: mergedOptions,
        telemetry: baseTelemetry,
      });

      if (inAppOutcome.status === 'success') {
        const cameraOutcome: PickImageOutcome = {
          status: 'success',
          result: {
            canceled: false,
            assets: [inAppOutcome.asset],
          },
        };
        addPickerBreadcrumb(PICKER_CAMERA_BREADCRUMB_CATEGORY, 'Câmera interna concluída.', 'info', {
          ...baseTelemetry,
          ...summarizeResult(cameraOutcome),
        });
        return cameraOutcome;
      }

      if (inAppOutcome.status === 'cancelled' || inAppOutcome.status === 'denied') {
        addPickerBreadcrumb(PICKER_CAMERA_BREADCRUMB_CATEGORY, 'Câmera interna cancelada ou negada.', 'warning', {
          ...baseTelemetry,
          pickerOutcome: inAppOutcome.status,
        });
        return { status: inAppOutcome.status };
      }

      throw inAppOutcome.error;
    }

    addPickerBreadcrumb(PICKER_CAMERA_BREADCRUMB_CATEGORY, 'Abrindo câmera nativa.', 'info', {
      ...baseTelemetry,
      permissionRequestMode,
      ...flattenCameraPermissionSnapshot('before', currentPermissionSnapshot),
      ...flattenCameraPermissionSnapshot('after', requestedPermissionSnapshot),
    });
    await persistPendingCameraContext(baseTelemetry);
    flushBufferedLogEvents();
    const cameraOutcome = await launchCamera(mergedOptions);
    await clearPendingCameraContext(`camera_native_launch_finished_${cameraOutcome.status}`, baseTelemetry);
    addPickerBreadcrumb(PICKER_CAMERA_BREADCRUMB_CATEGORY, 'Fluxo da câmera concluído.', 'info', {
      ...baseTelemetry,
      permissionRequestMode,
      ...summarizeResult(cameraOutcome),
    });
    return cameraOutcome;
  } catch (error) {
    const normalizedError = normalizeError(error);
    await clearPendingCameraContext('camera_wrapper_failed', baseTelemetry);
    addPickerBreadcrumb(PICKER_CAMERA_BREADCRUMB_CATEGORY, 'Falha inesperada ao abrir câmera.', 'error', {
      ...baseTelemetry,
      exceptionMessage: normalizedError.message,
    });
    captureSentryException(normalizedError, {
      ...baseTelemetry,
      flowStage: 'takePhotoFromCamera',
    });
    return { status: 'failed', error: normalizedError };
  }
};
